import { useState, useCallback, useEffect, useMemo } from 'react'
import { Link } from '@tanstack/react-router'
import { useTranslation } from 'react-i18next'
import { Button } from './ui/button'
import { Input } from './ui/input'
import { Badge } from './ui/badge'
import { Card, CardContent } from './ui/card'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select'
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from './ui/alert-dialog'
import { mdiCalendar, mdiChevronDown, mdiChevronUp, mdiEyeOutline, mdiInformationOutline, mdiMagnify, mdiPlus, mdiTrashCanOutline, mdiUnfoldMoreHorizontal } from '@mdi/js'
import { AppIcon } from './ui/icon'
import { useQueryClient } from '@tanstack/react-query'
import {
  useAllContracts,
  useAssetTypes,
  useContractTypes,
  useDeleteContract,
  useUploadDocument,
  type Contract,
} from '../hooks/useQueries'
import { $api } from '../api/queryClient'
import type { ContractChecklistContract, ContractChecklistRow } from '../api/schema'
import { useToast } from '../hooks/use-toast'
import { useLiveSubscription } from '../contexts/LiveEventsContext'
import { Can } from '../contexts/PermissionsContext'
import { EmptyState } from './ui/empty-state'
import { SECTION_ICONS } from '../lib/section-icons'
import { contractTypeGroups, contractTypeName, NO_CONTRACT_TYPE_GROUP } from '../lib/contract-groups'
import { ContractTypeGroupSection } from './ContractTypeGroupSection'
import { checklistGroups, type ChecklistGroup } from '../lib/contract-checklist'
import { STATUS_PILL_CONFIG } from './AssetCompliance'
import { ChecklistGroupHeader, ChecklistGroupNotes, ContractRowDates } from './ContractChecklistParts'
import { ContractWaiverDialog } from './ContractWaiverDialog'

const CHECKLIST_PATH = '/api/v1/organizations/{orgCode}/assets/{assetUid}/contract-checklist' as const

interface AssetContractsProps {
  org: string
  assetUid: string
  /** The asset's name, for the waiver dialog. */
  assetName?: string
  /**
   * Show the site contract checklist (spec 2026-10-11-17): `site` on a
   * building_group, `building` on a building or house.
   */
  checklist?: 'site' | 'building'
}

interface ContractFilters {
  search?: string
  contractTypeUid?: string
  status?: string
}

type DescendantMode = 'auto' | 'enabled' | 'disabled'

const STATUS_KEYS = ['draft', 'open', 'in_progress', 'closed'] as const

const STATUS_VARIANTS: Record<string, 'secondary' | 'default' | 'destructive'> = {
  draft: 'secondary',
  open: 'default',
  in_progress: 'default',
  closed: 'destructive',
}

export function AssetContracts({ org, assetUid, assetName, checklist }: AssetContractsProps) {
  const { t, i18n } = useTranslation('contracts')
  const { toast } = useToast()
  const queryClient = useQueryClient()

  // Live entity notifications: this view is a filtered slice of the org's
  // contracts collection (assetUid is a query param, not a distinct scope),
  // so the coarse collection subscription is correct here — same precedent
  // as AssetDocuments.
  useLiveSubscription({ entity: 'contracts' })

  const getLocalizedName = useCallback((obj: any): string => {
    const names = obj?.names || obj?.name
    if (!names) return '-'
    if (typeof names === 'string') return names || '-'
    const n = names as Record<string, string>
    return n[i18n.language] || n.en || n.fr || Object.values(n)[0] || '-'
  }, [i18n.language])

  // Read initial filter state from URL
  const getUrlParams = useCallback(() => {
    const sp = new URLSearchParams(window.location.search)
    return {
      search: sp.get('q') || undefined,
      contractTypeUid: sp.get('contract_type_uid') || undefined,
      status: sp.get('status') || undefined,
    } as ContractFilters
  }, [])

  const getUrlDescendantMode = useCallback(() => {
    const sp = new URLSearchParams(window.location.search)
    return (sp.get('descendants') as DescendantMode) || 'auto'
  }, [])

  const [filters, setFilters] = useState<ContractFilters>(getUrlParams)
  const [contractToDelete, setContractToDelete] = useState<any | null>(null)
  const [descendantMode, setDescendantMode] = useState<DescendantMode>(getUrlDescendantMode)
  const [sort, setSort] = useState<{ key: 'name' | 'period'; dir: 'asc' | 'desc' } | null>(null)

  const toggleSort = (key: 'name' | 'period') => {
    setSort(prev => (prev?.key === key
      ? { key, dir: prev.dir === 'asc' ? 'desc' : 'asc' }
      : { key, dir: 'asc' }))
  }

  // Sync state to URL
  useEffect(() => {
    const url = new URL(window.location.href)
    const set = (key: string, val: string | undefined) => {
      if (val) url.searchParams.set(key, val)
      else url.searchParams.delete(key)
    }
    set('q', filters.search)
    set('contract_type_uid', filters.contractTypeUid)
    set('status', filters.status)
    url.searchParams.delete('page')
    set('descendants', descendantMode !== 'auto' ? descendantMode : undefined)
    window.history.replaceState({}, '', url.toString())
  }, [filters, descendantMode])

  // Every contract, no pager: the tab groups them by contract type. Chain
  // heads only, so an amendment stands for its base.
  const directQuery = useAllContracts(org, {
    ...filters,
    assetUid,
    headsOnly: true,
    // A lightweight RELATION is an intervenant without contract (spec
    // 2026-10-25-25): listed on the Intervenants card, never here.
    lightweight: false,
    include: ['assets', 'contract_type'],
    enabled: descendantMode !== 'enabled',
  })

  // Descendant contracts query
  const descendantQuery = useAllContracts(org, {
    ...filters,
    assetUid,
    assetIncludeChildren: true,
    headsOnly: true,
    lightweight: false,
    include: ['assets', 'contract_type'],
    enabled: descendantMode === 'enabled' ||
      (descendantMode === 'auto' && directQuery.isSuccess && (directQuery.data?.length ?? 0) === 0),
  })

  // Determine which result set to show
  const showingDescendants = descendantMode === 'enabled' ||
    (descendantMode === 'auto' && (directQuery.data?.length ?? 0) === 0 && (descendantQuery.data?.length ?? 0) > 0)

  const activeQuery = showingDescendants ? descendantQuery : directQuery
  const contracts = activeQuery.data
  const isLoading = activeQuery.isLoading || (descendantMode === 'auto' && directQuery.isSuccess && (directQuery.data?.length ?? 0) === 0 && descendantQuery.isLoading)
  const error = activeQuery.error

  const { data: contractTypes = [] } = useContractTypes(org)
  const deleteMutation = useDeleteContract(org)

  // The site contract checklist. Out of the caller's scope (404) or failing,
  // the tab falls back to the plain groups.
  const checklistQuery = $api.useQuery('get', CHECKLIST_PATH, {
    params: { path: { orgCode: org, assetUid } },
  }, { enabled: !!checklist && !!assetUid, retry: false })
  const checklistData = checklist && !checklistQuery.error ? checklistQuery.data : undefined
  const checklistRows: ContractChecklistRow[] = useMemo(() => checklistData?.rows ?? [], [checklistData])
  const factsByUid = useMemo(
    () => new Map<string, ContractChecklistContract>((checklistData?.contracts ?? []).map((c) => [c.uid, c])),
    [checklistData],
  )
  const invalidateChecklist = () => queryClient.invalidateQueries({
    predicate: (q) => q.queryKey[0] === 'get' && q.queryKey[1] === CHECKLIST_PATH,
  })
  const { data: assetTypes = [] } = useAssetTypes(org)
  const assetTypeName = (code: string) => {
    const at = assetTypes.find((a) => a.code === code)
    return at ? getLocalizedName(at) : code
  }

  const uploadMutation = useUploadDocument(org)
  const [uploadedCodes, setUploadedCodes] = useState<Set<string>>(new Set())
  const uploadFor = async (typeCode: string, file: File) => {
    const formData = new FormData()
    formData.append('file', file)
    formData.append('asset_uid', assetUid)
    try {
      await uploadMutation.mutateAsync(formData)
      setUploadedCodes((prev) => new Set(prev).add(typeCode))
    } catch {
      toast({ title: t('detail.error_title'), description: t('checklist.upload_error'), variant: 'destructive' })
    }
  }

  const [waiveType, setWaiveType] = useState<{ uid: string; name: string } | null>(null)
  const restoreMutation = $api.useMutation('delete', '/api/v1/organizations/{orgCode}/assets/{assetUid}/contract-checklist/waivers/{contractTypeUid}', {
    onSuccess: () => { void invalidateChecklist() },
    onError: () => {
      toast({ title: t('detail.error_title'), description: t('checklist.restore_error'), variant: 'destructive' })
    },
  })
  const restore = (row: ContractChecklistRow) => {
    if (!row.waiver) return
    restoreMutation.mutate({
      params: { path: { orgCode: org, assetUid: row.waiver.asset_uid, contractTypeUid: row.contract_type.uid } },
    })
  }

  const getContractTitle = (contract: any) => {
    return contract?.name || contract?.code || t('list.untitled')
  }

  const sortIcon = (key: 'name' | 'period') => {
    if (sort?.key !== key) return <AppIcon path={mdiUnfoldMoreHorizontal} size="xs" tone="muted" className="opacity-40" />
    return sort.dir === 'asc'
      ? <AppIcon path={mdiChevronUp} size="xs" tone="muted" />
      : <AppIcon path={mdiChevronDown} size="xs" tone="muted" />
  }

  const getAssetNames = (contract: any): string[] => {
    if (!contract?.assets) return []
    return contract.assets
      .filter((ca: any) => ca.asset?.uid !== assetUid)
      .map((ca: any) => ca.asset?.name)
      .filter(Boolean)
  }

  // « BOUVIER · Bâtiment 1B +2 »: the provider, then the first covered asset
  // other than this one and how many more. Missing parts are omitted.
  const getProviderLine = (contract: Contract): string => {
    const others = getAssetNames(contract)
    const scope = others.length > 0 ? others[0] + (others.length > 1 ? ` +${others.length - 1}` : '') : ''
    return [contract.provider?.name, scope].filter(Boolean).join(' · ')
  }

  // Groups by contract type, rows by name (or the chosen sort) inside each.
  const groups = useMemo(() => {
    const title = (c: Contract) => (c.name || c.code || '').toString()
    const byName = (a: Contract, b: Contract) => title(a).localeCompare(title(b), i18n.language)
    const compare = (a: Contract, b: Contract): number => {
      if (sort?.key === 'period') {
        const da = a.start_date ?? '', db = b.start_date ?? ''
        if (da !== db) {
          if (!da) return 1
          if (!db) return -1
          return sort.dir === 'asc' ? da.localeCompare(db) : db.localeCompare(da)
        }
        return byName(a, b)
      }
      return sort?.dir === 'desc' ? byName(b, a) : byName(a, b)
    }
    const plain = contractTypeGroups([...contracts].sort(compare), checklistRows.map((r) => r.contract_type.code), {
      locale: i18n.language,
      types: contractTypes,
    })
    const filtered = !!(filters.search || filters.contractTypeUid || filters.status)
    return checklistGroups<Contract>(plain, checklistRows, factsByUid, {
      locale: i18n.language,
      today: new Date().toLocaleDateString('sv-SE'),
      filtered,
      sorted: !!sort,
    })
  }, [contracts, sort, i18n.language, contractTypes, checklistRows, factsByUid, filters])

  const summary = checklistData?.summary
  const summaryLine = summary
    ? (['missing', 'partial', 'covered', 'not_applicable'] as const)
      .filter((k) => summary[k] > 0)
      .map((k) => t(`checklist.summary_${k}`, { count: summary[k] }))
      .join(' · ')
    : ''

  const getStatusVariant = (status: string) => {
    return STATUS_VARIANTS[status] || 'secondary'
  }

  const getStatusLabel = (status: string) => {
    return t(`status.${status}`, status)
  }

  const handleSearch = (value: string) => {
    setFilters(prev => ({ ...prev, search: value || undefined }))
  }

  const handleContractTypeChange = (value: string) => {
    setFilters(prev => ({
      ...prev,
      contractTypeUid: value === 'all' ? undefined : value
    }))
  }

  const handleStatusChange = (value: string) => {
    setFilters(prev => ({
      ...prev,
      status: value === 'all' ? undefined : value
    }))
  }

  const handleDelete = async (contract: any) => {
    setContractToDelete(contract)
  }

  const confirmDelete = async () => {
    if (!contractToDelete) return

    try {
      await deleteMutation.mutateAsync(contractToDelete.uid)
      setContractToDelete(null)
    } catch {
      toast({
        title: t('detail.error_title'),
        description: t('asset_tab.delete_error'),
        variant: "destructive"
      })
    }
  }

  const cancelDelete = () => {
    setContractToDelete(null)
  }

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString()
  }

  const redRow = STATUS_PILL_CONFIG.NON_COMPLIANT.bg

  // A contract the tab lists: its name, provider line, the checklist's dates.
  const renderListedRow = (contract: Contract, facts: ContractChecklistContract | undefined) => {
    const providerLine = getProviderLine(contract)
    return (
      <tr key={contract.uid} className={facts?.follow_up ? redRow : 'hover:bg-muted/50'} data-testid={`contract-row-${contract.uid}`}>
        <td className="px-4 py-3">
          <div className="min-w-0">
            <Link
              to="/$org/contracts/$contractUid"
              params={{ org, contractUid: contract.uid }}
              className="break-words text-sm font-medium text-foreground hover:text-primary hover:underline transition-colors"
              data-testid={`contract-title-${contract.uid}`}
            >
              {getContractTitle(contract)}
            </Link>
            {providerLine && (
              <div className="mt-0.5 break-words text-xs text-muted-foreground" data-testid={`contract-provider-${contract.uid}`}>
                {providerLine}
              </div>
            )}
            {facts && <ContractRowDates org={org} facts={facts} />}
            {contract.code && (
              <div className="mt-1">
                <Badge variant="secondary" className="max-w-full truncate text-xs">
                  {contract.code}
                </Badge>
              </div>
            )}
          </div>
        </td>
        <td className="px-4 py-3">
          <Badge variant={getStatusVariant(contract.status || 'draft')} data-testid={`contract-status-${contract.uid}`}>
            {getStatusLabel(contract.status || 'draft')}
          </Badge>
        </td>
        <td className="px-4 py-3">
          <div className="text-sm text-muted-foreground">
            {contract.start_date && (
              <div className="flex items-center">
                <AppIcon path={mdiCalendar} size="xs" className="mr-1" />
                {formatDate(contract.start_date)}
              </div>
            )}
            {contract.end_date && (
              <div className="text-xs">
                {t('list.period_to')} {formatDate(contract.end_date)}
              </div>
            )}
          </div>
        </td>
        <td className="px-4 py-3">
          <div className="flex items-center space-x-2">
            <Link to="/$org/contracts/$contractUid" params={{ org, contractUid: contract.uid }}>
              <Button variant="ghost" size="sm" data-testid={`view-contract-${contract.uid}`}>
                <AppIcon path={mdiEyeOutline} size="sm" />
              </Button>
            </Link>
            <Button
              variant="ghost"
              size="sm"
              onClick={() => handleDelete(contract)}
              disabled={deleteMutation.isPending}
              data-testid={`delete-contract-${contract.uid}`}
            >
              <AppIcon path={mdiTrashCanOutline} size="sm" />
            </Button>
          </div>
        </td>
      </tr>
    )
  }

  // A contract the checklist counts but the tab does not list: held by the
  // site above, or on a descendant while the descendant toggle is off.
  const renderHeldRow = (facts: ContractChecklistContract) => {
    const line = [facts.provider?.name, facts.held_by?.name].filter(Boolean).join(' · ')
    return (
      <tr key={facts.uid} className={facts.follow_up ? redRow : 'hover:bg-muted/50'} data-testid={`contract-row-${facts.uid}`}>
        <td className="px-4 py-3" colSpan={3}>
          <div className="min-w-0">
            <Link
              to="/$org/contracts/$contractUid"
              params={{ org, contractUid: facts.uid }}
              className="break-words text-sm font-medium text-foreground hover:text-primary hover:underline transition-colors"
              data-testid={`contract-title-${facts.uid}`}
            >
              {facts.name || t('list.untitled')}
            </Link>
            {line && (
              <div className="mt-0.5 break-words text-xs text-muted-foreground" data-testid={`contract-provider-${facts.uid}`}>
                {line}
              </div>
            )}
            <ContractRowDates org={org} facts={facts} />
          </div>
        </td>
        <td className="px-4 py-3">
          <Link to="/$org/contracts/$contractUid" params={{ org, contractUid: facts.uid }}>
            <Button variant="ghost" size="sm" data-testid={`view-contract-${facts.uid}`}>
              <AppIcon path={mdiEyeOutline} size="sm" />
            </Button>
          </Link>
        </td>
      </tr>
    )
  }

  const renderGroup = (group: ChecklistGroup<Contract>) => {
    const title = group.typeCode === NO_CONTRACT_TYPE_GROUP
      ? t('asset_tab.group_none')
      : contractTypeName(group.contractType, i18n.language) || group.typeCode
    const row = group.row
    return (
      <ContractTypeGroupSection
        key={group.typeCode}
        typeCode={group.typeCode}
        title={title}
        countLabel={t('asset_tab.group_count', { count: group.items.length })}
        toggleLabel={t('asset_tab.group_toggle', { name: title })}
        muted={row?.status === 'not_applicable'}
        headerSlot={row && (
          <ChecklistGroupHeader
            org={org}
            assetUid={assetUid}
            row={row}
            factsByUid={factsByUid}
            assetTypeName={assetTypeName}
            onUpload={(file) => { void uploadFor(group.typeCode, file) }}
            onWaive={() => setWaiveType({ uid: row.contract_type.uid, name: title })}
            onRestore={() => restore(row)}
            restoring={restoreMutation.isPending}
          />
        )}
      >
        {row && (
          <ChecklistGroupNotes org={org} row={row} empty={group.items.length === 0} uploaded={uploadedCodes.has(group.typeCode)} />
        )}
        {group.items.length > 0 && (
          <table className="w-full table-fixed">
            <ContractColumns />
            <tbody className="bg-card divide-y divide-border">
              {group.items.map((item) => (item.contract
                ? renderListedRow(item.contract, item.facts)
                : item.facts && renderHeldRow(item.facts)))}
            </tbody>
          </table>
        )}
      </ContractTypeGroupSection>
    )
  }

  return (
    <div className="space-y-6">
      {/* The checklist summary on the left, the action button top-right */}
      <div className="flex flex-wrap items-center justify-end gap-2">
        {summaryLine && (
          <p className="mr-auto text-sm font-medium text-foreground" data-testid="contract-checklist-summary">
            {summaryLine}
          </p>
        )}
        <Can perm="contracts.write">
        <Link
          to="/$org/contracts/create"
          params={{ org }}
          search={{ asset_uid: assetUid }}
          data-testid="create-contract-btn"
        >
          <Button
            type="button"
            size="sm"
            aria-label={t('common:actions.add_contract', 'Add a contract')}
            title={t('common:actions.add_contract', 'Add a contract')}
          >
            <AppIcon path={mdiPlus} size="sm" className="md:mr-1" />
            <span className="hidden md:inline">{t('common:actions.add_contract', 'Add a contract')}</span>
          </Button>
        </Link>
        </Can>
      </div>

      {/* Filters box */}
      <div className="flex flex-wrap items-center gap-2 rounded-lg border border-border bg-card p-3">
        {/* Search */}
        <div className="relative w-full sm:w-56">
          <AppIcon path={mdiMagnify} size="sm" className="absolute left-3 top-1/2 -translate-y-1/2" />
          <Input
            placeholder={t('list.search_placeholder')}
            value={filters.search || ''}
            onChange={(e) => handleSearch(e.target.value)}
            className="pl-9 h-9"
            data-testid="search-input"
          />
        </div>

        {/* Contract Type Filter */}
        <Select value={filters.contractTypeUid || 'all'} onValueChange={handleContractTypeChange}>
          <SelectTrigger className="w-auto h-9 gap-1" data-testid="contract-type-filter">
            <SelectValue placeholder={t('list.all_contract_types')} />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">{t('list.all_contract_types')}</SelectItem>
            {contractTypes.map((type) => (
              <SelectItem key={type.uid} value={type.uid}>
                {getLocalizedName(type)}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>

        {/* Status Filter */}
        <Select value={filters.status || 'all'} onValueChange={handleStatusChange}>
          <SelectTrigger className="w-auto h-9 gap-1" data-testid="status-filter">
            <SelectValue placeholder={t('list.all_statuses')} />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">{t('list.all_statuses')}</SelectItem>
            {STATUS_KEYS.map((status) => (
              <SelectItem key={status} value={status}>
                {t(`status.${status}`)}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>

        {/* Descendant Mode Filter */}
        <Select value={descendantMode} onValueChange={(v) => setDescendantMode(v as DescendantMode)}>
          <SelectTrigger className="w-auto h-9 gap-1" data-testid="descendant-mode-filter">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="auto">{t('asset_tab.descendant_auto')}</SelectItem>
            <SelectItem value="enabled">{t('asset_tab.descendant_enabled')}</SelectItem>
            <SelectItem value="disabled">{t('asset_tab.descendant_disabled')}</SelectItem>
          </SelectContent>
        </Select>
      </div>

      {/* Info banner when showing descendants in auto mode */}
      {descendantMode === 'auto' && showingDescendants && (
        <div className="flex items-center gap-2 rounded-md border border-blue-200 bg-blue-50 dark:border-blue-800 dark:bg-blue-950 p-3 text-sm text-blue-700 dark:text-blue-300">
          <AppIcon path={mdiInformationOutline} size="sm" className="shrink-0" />
          {t('asset_tab.showing_descendants')}
        </div>
      )}

      {checklist === 'site' && checklistData && checklistRows.length === 0 && (
        <EmptyState
          variant="inline"
          icon={SECTION_ICONS.contracts}
          title={t('checklist.empty_site_title')}
          description={t('checklist.empty_site_description')}
          action={
            <Can perm="configuration.write">
              <Link to="/$org/contract-types" params={{ org }} data-testid="contract-checklist-settings-link">
                <Button type="button" size="sm" variant="outline">{t('checklist.settings_link')}</Button>
              </Link>
            </Can>
          }
          data-testid="contract-checklist-empty"
        />
      )}

      {/* Contracts List */}
      <Card>
        <CardContent className="p-0">
          {isLoading ? (
            <div className="flex items-center justify-center h-64">
              <div className="motion-safe:animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
            </div>
          ) : error ? (
            <div className="flex flex-col items-center justify-center h-64">
              <p className="text-destructive mb-2">
                {t('list.error')}
              </p>
              <p className="text-sm text-muted-foreground">
                {(error as any)?.title || (error as any)?.message || 'Unknown error occurred'}
              </p>
            </div>
          ) : groups.length > 0 ? (
            <div className="overflow-x-auto">
              <div className="min-w-[560px]">
                <table className="w-full table-fixed">
                  <ContractColumns />
                  <thead className="bg-muted/50">
                    <tr>
                      <th className="px-4 py-3 text-left text-[11px] font-medium text-muted-foreground uppercase tracking-wider">
                        <button
                          type="button"
                          onClick={() => toggleSort('name')}
                          className="inline-flex items-center gap-1 uppercase tracking-wider hover:text-foreground transition-colors"
                          data-testid="sort-contract"
                        >
                          {t('list.column_contract')}
                          {sortIcon('name')}
                        </button>
                      </th>
                      <th className="px-4 py-3 text-left text-[11px] font-medium text-muted-foreground uppercase tracking-wider">
                        {t('list.column_status')}
                      </th>
                      <th className="px-4 py-3 text-left text-[11px] font-medium text-muted-foreground uppercase tracking-wider">
                        <button
                          type="button"
                          onClick={() => toggleSort('period')}
                          className="inline-flex items-center gap-1 uppercase tracking-wider hover:text-foreground transition-colors"
                          data-testid="sort-period"
                        >
                          {t('list.column_period')}
                          {sortIcon('period')}
                        </button>
                      </th>
                      <th className="px-4 py-3 text-left text-[11px] font-medium text-muted-foreground uppercase tracking-wider">
                        {t('list.column_actions')}
                      </th>
                    </tr>
                  </thead>
                </table>
                {groups.map((group) => renderGroup(group))}
              </div>
            </div>
          ) : (
            <EmptyState
              variant="inline"
              className="py-12"
              icon={SECTION_ICONS.contracts}
              title={t('asset_tab.no_contracts')}
              data-testid="asset-contracts-empty"
            />
          )}
        </CardContent>
      </Card>

      {/* Confirmation Dialog for Deleting Contract */}
      <AlertDialog open={contractToDelete !== null} onOpenChange={(open) => !open && cancelDelete()}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>{t('asset_tab.delete_title')}</AlertDialogTitle>
            <AlertDialogDescription>
              {t('asset_tab.delete_description', { name: contractToDelete ? getContractTitle(contractToDelete) : '' })}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel onClick={cancelDelete}>{t('asset_tab.delete_cancel')}</AlertDialogCancel>
            <AlertDialogAction
              onClick={confirmDelete}
              className="bg-destructive hover:bg-destructive/90"
            >
              {t('asset_tab.delete_confirm')}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <ContractWaiverDialog
        org={org}
        assetUid={assetUid}
        assetName={assetName ?? ''}
        contractType={waiveType}
        onOpenChange={(open) => { if (!open) setWaiveType(null) }}
        onDone={() => { void invalidateChecklist() }}
      />
    </div>
  )
}

// The columns every group's table shares with the header table, so they line up.
function ContractColumns() {
  return (
    <colgroup>
      <col />
      <col className="w-28" />
      <col className="w-36" />
      <col className="w-28" />
    </colgroup>
  )
}
